import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import md5 from 'crypto-js/md5';
import { useCallback, useEffect, useRef } from 'react';

export function useFlashSaleTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  const enterTimer = useRef<number>(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });

  // 生成活动标识 MD5
  const marketActivityId = md5('SpringSale').toString();

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_View
   * @EventDesc Flash Sale活动页页面浏览
   * @EventParams {
   *   page_name: 页面名称,
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   */
  const trackPageView = useCallback(() => {
    let { refer_page_name, refer_url_path } = getFromRouterInfo();
    enterTimer.current = Date.now();

    // 如果从路由信息中取不到值，从URL查询参数中获取
    if (!refer_page_name || !refer_url_path) {
      if (typeof window !== 'undefined') {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const paramReferPageName = urlParams.get('refer_page_name');
          const paramReferUrlPath = urlParams.get('refer_url_path');

          if (paramReferPageName) {
            refer_page_name = paramReferPageName;
          }
          if (paramReferUrlPath) {
            refer_url_path = paramReferUrlPath;
          }

          // 如果查询参数也没有，使用默认值
          if (!refer_page_name) {
            refer_page_name = '';
          }
          if (!refer_url_path) {
            refer_url_path = '';
          }
        } catch (error) {
          logger.error('Failed to get URL params:', error);
          refer_page_name = refer_page_name || '';
          refer_url_path = refer_url_path || '';
        }
      }
    }

    try {
      logger.log('trackPageView', refer_page_name, refer_url_path);
      trackEvent(
        {
          eventName: 'CampaignPage_View',
          properties: {
            page_name: '202603春促促销会场',
            refer_page_name,
            refer_url_path,
            market_activity_id: marketActivityId,
            market_activity_name: '26年3月春促活动',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Flash Sale page view tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_Leave
   * @EventDesc Flash Sale活动页页面离开
   * @EventParams {
   *   page_name: 页面名称,
   *   view_advance: 浏览进度,
   *   view_duration: 浏览时长,
   *   view_area: 浏览区域,
   * }
   */
  const trackPageLeave = useCallback(() => {
    // 使用保存的滚动状态
    const { viewAdvance, viewArea } = lastScrollState.current;

    try {
      logger.log('trackPageLeave', viewAdvance, viewArea);
      trackEvent(
        {
          eventName: 'CampaignPage_Leave',
          properties: {
            page_name: '202603春促促销会场',
            view_advance: viewAdvance,
            view_duration: Math.ceil((Date.now() - enterTimer.current) / 1000),
            view_area: viewArea,
            market_activity_id: marketActivityId,
            market_activity_name: '26年3月春促活动',
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Flash Sale page leave tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Click
   * @EventDesc Flash Sale限购区商品点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   product_sku: 商品sku,
   *   business_sku: 平台sku,
   *   shop_sku: 站点商品sku,
   *   product_id: 商品id,
   *   product_name: 商品名称,
   *   product_price: 商品原价,
   *   current_product_price: 商品现价,
   *   base_current_product_price: 基础货币现价,
   *   dollar_exchange_rate: 美元汇率,
   *   currency: 货币,
   * }
   */
  const trackFlashSaleProductClick = useCallback(
    (item: any, index: number) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties: {
              page_name: '202603春促促销会场',
              module_name: '限购区',
              pits_index: index,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_id: `${item.id}`,
              product_name: item.short_name,
              product_price: parseFloat(nowPriceInfo.price),
              current_product_price: parseFloat(nowPriceInfo.sell_price),
              base_current_product_price: parseFloat(basePriceInfo.sell_price),
              dollar_exchange_rate: parseFloat(currency.exchange_rate),
              currency: currency.name,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Flash Sale product click tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Exp
   * @EventDesc Flash Sale限购区商品曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   product_sku: 商品sku,
   *   business_sku: 平台sku,
   *   shop_sku: 站点商品sku,
   *   product_id: 商品id,
   *   product_name: 商品名称,
   *   product_price: 商品原价,
   *   current_product_price: 商品现价,
   *   base_current_product_price: 基础货币现价,
   *   dollar_exchange_rate: 美元汇率,
   *   currency: 货币,
   * }
   */
  const trackFlashSaleProductExp = useCallback(
    (item: any, index: number) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties: {
              page_name: '202603春促促销会场',
              module_name: '限购区',
              pits_index: index,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_id: `${item.id}`,
              product_name: item.short_name,
              product_price: parseFloat(nowPriceInfo.price),
              current_product_price: parseFloat(nowPriceInfo.sell_price),
              base_current_product_price: parseFloat(basePriceInfo.sell_price),
              dollar_exchange_rate: parseFloat(currency.exchange_rate),
              currency: currency.name,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Flash Sale product exp tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Click
   * @EventDesc Promotion促销区商品点击
   */
  const trackPromotionProductClick = useCallback(
    (item: any, index: number) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties: {
              page_name: '202603春促促销会场',
              module_name: '促销区',
              pits_index: index,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_id: `${item.id}`,
              product_name: item.short_name,
              product_price: parseFloat(nowPriceInfo.price),
              current_product_price: parseFloat(nowPriceInfo.sell_price),
              base_current_product_price: parseFloat(basePriceInfo.sell_price),
              dollar_exchange_rate: parseFloat(currency.exchange_rate),
              currency: currency.name,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Promotion product click tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Exp
   * @EventDesc Promotion促销区商品曝光
   */
  const trackPromotionProductExp = useCallback(
    (item: any, index: number) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties: {
              page_name: '202603春促促销会场',
              module_name: '促销区',
              pits_index: index,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_id: `${item.id}`,
              product_name: item.short_name,
              product_price: parseFloat(nowPriceInfo.price),
              current_product_price: parseFloat(nowPriceInfo.sell_price),
              base_current_product_price: parseFloat(basePriceInfo.sell_price),
              dollar_exchange_rate: parseFloat(currency.exchange_rate),
              currency: currency.name,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Promotion product exp tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageAD_Click
   * @EventDesc 底部banner广告位点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   pits_jump_type: 坑位内容的跳转类型,
   *   pits_jump_content: 坑位内容跳转的内容,
   * }
   */
  const trackBottomBannerClick = useCallback(
    (item: any, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageAD_Click',
            properties: {
              page_name: '202603春促促销会场',
              module_name: '底部banner位',
              pits_index: index,
              pits_jump_type: `${item.jump_content?.jump_type}` || '',
              pits_jump_content: `${item.jump_content?.Jump_target}` || '',
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Bottom banner click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageAD_Exp
   * @EventDesc 底部banner广告位曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   pits_jump_type: 坑位内容的跳转类型,
   *   pits_jump_content: 坑位内容跳转的内容,
   * }
   */
  const trackBottomBannerExp = useCallback(
    (item: any, index: number) => {
      try {
        trackEvent(
          {
            eventName: 'CampaignPageAD_Exp',
            properties: {
              page_name: '202603春促促销会场',
              module_name: '底部banner位',
              pits_index: index,
              pits_jump_type: `${item.jump_content?.jump_type}` || '',
              pits_jump_content: `${item.jump_content?.Jump_target}` || '',
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Bottom banner exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  // YouMightLike猜你喜欢商品点击埋点
  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Click
   * @EventDesc YouMightLike猜你喜欢商品点击
   */
  const trackYouMightLikeProductClick = useCallback(
    (item: any, index: number) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties: {
              page_name: '202603春促促销会场',
              module_name: '你也喜欢',
              pits_index: index,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_id: `${item.id}`,
              product_name: item.short_name,
              product_price: parseFloat(nowPriceInfo.price),
              current_product_price: parseFloat(nowPriceInfo.sell_price),
              base_current_product_price: parseFloat(basePriceInfo.sell_price),
              dollar_exchange_rate: parseFloat(currency.exchange_rate),
              currency: currency.name,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('YouMightLike product click tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Exp
   * @EventDesc YouMightLike猜你喜欢商品曝光
   */
  const trackYouMightLikeProductExp = useCallback(
    (item: any, index: number) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties: {
              page_name: '202603春促促销会场',
              module_name: '你也喜欢',
              pits_index: index,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_id: `${item.id}`,
              product_name: item.short_name,
              product_price: parseFloat(nowPriceInfo.price),
              current_product_price: parseFloat(nowPriceInfo.sell_price),
              base_current_product_price: parseFloat(basePriceInfo.sell_price),
              dollar_exchange_rate: parseFloat(currency.exchange_rate),
              currency: currency.name,
              market_activity_id: marketActivityId,
              market_activity_name: '26年3月春促活动',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('YouMightLike product exp tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  // 定期更新滚动状态，避免页面跳转时丢失滚动信息（参考denim-glasses实现）
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        const viewArea = getViewArea();
        lastScrollState.current = { viewAdvance, viewArea };
      } catch (error) {
        // 忽略错误，使用默认值
        logger.error('Error updating scroll state:', error);
      }
    };

    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，实时更新滚动状态
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    trackPageView,
    trackPageLeave,
    trackFlashSaleProductClick,
    trackFlashSaleProductExp,
    trackPromotionProductClick,
    trackPromotionProductExp,
    trackBottomBannerClick,
    trackBottomBannerExp,
    trackYouMightLikeProductClick,
    trackYouMightLikeProductExp,
  };
}
